import React, { useMemo } from "react"
import { Card, Text, Icon, UnstyledButton } from "@/components"
import { useStyles } from "@/components/ActionCard/ActionCard.styles"
import { useMantineTheme, Box } from "@mantine/core"

interface ActionCardProps {
    title: string
    dataCy?: string
    description?: string
    isSelected?: boolean
    icon?: string
    variant?: "success" | "error" | "warning" | "default"
    onClick?: () => void
}

export const ActionCard = ({
    title,
    dataCy,
    description,
    isSelected,
    icon,
    onClick,
    variant = "default",
}: ActionCardProps) => {
    const theme = useMantineTheme()

    const mainColor = useMemo(() => {
        switch (variant) {
            case "success":
                return theme.colors.green[5]
            case "warning":
                return theme.colors.yellow[5]
            case "error":
                return theme.colors.red[5]
            default:
                return theme.colors.violet[5]
        }
    }, [variant])

    const borderColor = useMemo(() => {
        switch (variant) {
            case "success":
                return theme.colors.green[2]
            case "warning":
                return theme.colors.yellow[2]
            case "error":
                return theme.colors.red[2]
            default:
                return theme.colors.violet[3]
        }
    }, [variant])

    const isClickable = onClick !== undefined

    const { classes } = useStyles({
        isSelected,
        borderColor,
    })

    const card = (
        <Card className={classes.container} onClick={onClick} data-cy={dataCy}>
            {icon && <Icon className={classes.icon} color={mainColor} size="lg" icon={icon} shadow="lg" />}

            <Box className={classes.contentContainer}>
                <Text className={classes.title}>{title}</Text>

                {description && <Text className={classes.description}>{description}</Text>}
            </Box>
        </Card>
    )

    if (isClickable) return <UnstyledButton className={classes.unstyledButton}>{card}</UnstyledButton>

    return card
}
